/**
 * The wording of the front of the snapshot form that is NOT consent or disclosure text: the
 * basics page, the sign-in step, the second check and the tax-returns step's own controls
 * (crossmarc/steward-app#2158). What a person is told about sharing lives in `consentCopy.ts`
 * and is not to be mixed in here.
 *
 * No string in this file may contain an em dash or an en dash, "bank-grade", or a claim that
 * data never leaves anywhere (`consentCopy.test.ts` checks every export of both files).
 */
import { SIGN_IN_STEP_BODY, SIGN_IN_STEP_REFUSED_BODY } from './consentCopy';

// Cover -----------------------------------------------------------------------------------

/** Under the cover's button, for a person who has been here before. */
export const COVER_RETURNING_PROMPT = 'Already started?';
export const COVER_RETURNING_LINK = 'Sign in to carry on';

// Basics ----------------------------------------------------------------------------------

export const BASICS_HEADING = 'Start with the basics';
export const BASICS_FIRST_NAME_LABEL = 'First name';
export const BASICS_LAST_NAME_LABEL = 'Last name';
export const BASICS_EMAIL_LABEL = 'Email';
export const BASICS_PHONE_LABEL = 'Phone (optional)';
/**
 * Under the email when the invitation named it and the field is read-only (crossmarc/steward-app#2402).
 * Wording approved by Matt, 2026-10-14. Honest to #2176: another address still fills the snapshot.
 */
export const BASICS_EMAIL_INVITED_HELP =
  'Your invitation was sent to this address. If you want to use a different one, you can still fill in the snapshot, but it will not be connected to this invitation.';
export const BASICS_CONTINUE_LABEL = 'Continue';
export const BASICS_SENDING_LABEL = 'One moment…';
export const BASICS_BACK_LABEL = 'Back';
export const BASICS_FIRST_NAME_NEEDED = 'Please enter your first name.';
export const BASICS_LAST_NAME_NEEDED = 'Please enter your last name.';
export const BASICS_EMAIL_NEEDED = 'Please enter an email address we can reach you at.';
/** A phone was typed that the server would not accept. Blank is always fine. */
export const BASICS_PHONE_NEEDED = 'Please check the phone number, or leave it blank.';
/** No answer came back from the server at all. The one case the page does not move on. */
export const BASICS_UNREACHABLE =
  'We could not reach our server just now, so your details have not been sent. Please check your '
  + 'connection and try again.';
export const BASICS_TURNSTILE_PENDING =
  'Please wait a moment for the security check to finish, then try again.';
export const BASICS_TURNSTILE_FAILED =
  'We could not run the security check, so we have not sent anything. Please refresh this page and '
  + 'try once more.';
export const BASICS_TURNSTILE_RUNNING = 'Running a quick security check. This usually takes a moment.';

// Sign-in step ----------------------------------------------------------------------------

/**
 * The snapshot's wording for the sign-in card, laid over the card's neutral defaults. Moved
 * here from the retired `/signin-preview` page; the body and the refused line are consent
 * drafts and come from `consentCopy.ts`.
 *
 * A stable module-level object, as the card asks for. Not annotated with the card's
 * `SignInCopyOverrides` type on purpose: `IntakeSignInStep.tsx` is the one file in the app
 * that imports the sign-in module, and it is where this object is checked against that type
 * (it is passed as the card's `copy`).
 */
export const SNAPSHOT_SIGN_IN_COPY = {
  create: {
    heading: (firstName?: string) => {
      const name = firstName?.trim();
      return name ? `Thanks, ${name}. Let us protect your information.` : 'Let us protect your information.';
    },
    body: SIGN_IN_STEP_BODY,
    haveLoginLink: 'Use the same sign-in here.',
  },
  refused: {
    body: SIGN_IN_STEP_REFUSED_BODY,
  },
};

/** The sign-in step's Back label when Back lands on the basics page (the details). */
export const SIGN_IN_BACK_LABEL = 'Back to your details';
/** The same control when Back lands anywhere else (the cover, or a fresh start): `IntakeFlow.tsx`. */
export const SIGN_IN_PLAIN_BACK_LABEL = 'Back';
/** Shown above the card when a sign-in ran out while the form was open. */
export const SIGN_IN_ENDED_NOTICE = 'Your sign-in ended. Please sign in again to carry on.';
/** Under the notice above, when the last changes could not be confirmed as saved first. */
export const SIGN_IN_ENDED_UNSAVED_NOTICE = 'Your last changes may not have been saved.';

// The form will not open for this login ------------------------------------------------------
// Shown when a draft call refused a login (401 or 403 FORBIDDEN) while the note of an earlier
// refusal of the same login was still standing, which is to say the form had not opened for
// that login in between (`flowState.endedByRefusal`). An idle timeout or a chosen sign-out
// does not count toward it. Nothing is tried again until they press the button.
export const FORM_BLOCKED_HEADING = 'We cannot open your form right now';
export function formBlockedBody(firmName: string): string {
  return `We could not open the form for this login. Please try again in a little while. If it keeps happening, let ${firmName} know.`;
}
export const FORM_BLOCKED_START_AGAIN_LABEL = 'Back to the start';

// An invitation link that is no longer active (crossmarc/steward-app#2731) ----------------------
// Shown for a definite not-found or gone answer to the link's code. One page for a cancelled,
// replaced, used, expired or never-issued link, so the public route reveals none of them.
export const INVITATION_INACTIVE_HEADING = 'This link is no longer active';
export const INVITATION_INACTIVE_BODY =
  'Your invitation may have been cancelled or replaced. Please contact your advisor, who can send you a new link.';
// No answer, or a server error: the link may be fine, so nothing is said about it.
export const INVITATION_UNAVAILABLE_HEADING = 'We could not load your invitation';
export const INVITATION_UNAVAILABLE_BODY = 'Please check your connection and try again.';
export const INVITATION_UNAVAILABLE_RETRY_LABEL = 'Try again';

// A Google login we could not set up (crossmarc/steward-app#2404) ------------------------------
// A Google login is set up with us (`POST /me/provision`) the first time the form is opened
// with it. These are the answers to that setup that the person can act on. None of them
// signs the person out on being shown: the button does (and brings them back to the front
// steps), and so does the idle timeout if they leave the page open.
/** `403 NOT_ELIGIBLE` when this tab's basics carry an address that is not the Google login's. Approved by Matt 2026-10-14. */
export const GOOGLE_ADDRESS_MISMATCH_HEADING = 'Use the address you gave us';
export const GOOGLE_ADDRESS_MISMATCH_BODY =
  'The Google account you chose uses a different email address from the one on your details. '
  + 'Please continue with Google using that address, or create a login with your email instead.';
/** `403 NOT_ELIGIBLE` with no basics in this tab, or the same address (a lapsed grant). Matt's text, 2026-10-14. */
export const GOOGLE_DETAILS_FIRST_HEADING = 'Start with your details';
export const GOOGLE_DETAILS_FIRST_BODY =
  'Please go back and enter your details first, then continue with Google using the same email address.';
/** The Google address already belongs to a login (one with a password). Approved by Matt 2026-10-14. */
export const GOOGLE_ACCOUNT_EXISTS_HEADING = 'Sign in with your password';
export const GOOGLE_ACCOUNT_EXISTS_BODY =
  'You already have a login with this email address. Please sign in with your email and password instead.';
/**
 * The setup was answered `LINK_PROOF_REPLAYED` or `LINK_PROOF_EXPIRED` and the login is still
 * not set up (#2408): the only text on that answer. Its one button is the sign-in card's own
 * Google label (`GOOGLE_SIGN_IN_LABEL`, `auth/googleSignIn.ts`), not new copy. Matt's text, 2026-10-14.
 */
export const GOOGLE_SIGN_IN_AGAIN = 'Please sign in with Google again';
/** The one button under the other setup answers: signs the Google login out and goes back to the front steps. Approved by Matt 2026-10-14. */
export const GOOGLE_SETUP_BACK_LABEL = 'Go back';

// Signing out with changes that could not be saved -------------------------------------------
export const SIGN_OUT_UNSAVED_HEADING = 'Sign out without saving your last changes?';
export const SIGN_OUT_UNSAVED_BODY =
  'We could not save your most recent changes. If you sign out now they may be lost. Everything saved before that is kept.';
export const SIGN_OUT_UNSAVED_STAY_LABEL = 'Stay and try again';
export const SIGN_OUT_UNSAVED_LEAVE_LABEL = 'Sign out anyway';

// Second check ----------------------------------------------------------------------------

export const SECOND_CHECK_LOADING_HEADING = 'Opening your form';
export const SECOND_CHECK_LOADING_BODY = 'One moment while we check how you sign in.';
export const SECOND_CHECK_LOAD_FAILED =
  'We could not check how you sign in just now. Please try again in a moment.';
export const SECOND_CHECK_RETRY_LABEL = 'Try again';
/** A person who already has a passkey or an authenticator app. */
export const SECOND_CHECK_RETURNING_HEADING = 'Confirm it is you';
export const SECOND_CHECK_RETURNING_BODY =
  'Before we open your answers, we ask for your passkey or a code from your authenticator app.';
export const SECOND_CHECK_NOT_CONFIRMED = 'We could not confirm it was you just now. Please try again.';
// The form's own prompt (`IntakeSecondCheckPrompt`), for a person who already has a factor.
export const SECOND_CHECK_PASSKEY_BUTTON = 'Use your passkey';
export const SECOND_CHECK_PASSKEY_WAITING = 'Waiting for your passkey…';
export const SECOND_CHECK_PASSKEY_RETRY = 'Try your passkey again';
export const SECOND_CHECK_PASSKEY_CANCELLED = 'The passkey prompt was closed or timed out. You can try again.';
export const SECOND_CHECK_PASSKEY_FAILED = 'We could not confirm it was you with your passkey. Please try again.';
export const SECOND_CHECK_PASSKEY_UNSUPPORTED =
  'This browser cannot use a passkey. Please try another browser or device.';
export const SECOND_CHECK_CODE_INSTEAD = 'Use a code from your authenticator app instead';
export const SECOND_CHECK_CODE_LABEL = 'Code from your authenticator app';
export const SECOND_CHECK_CODE_BUTTON = 'Confirm';
export const SECOND_CHECK_CODE_CHECKING = 'Checking…';
/** The server said the code is not valid. A code that has run out looks the same to it. */
export const SECOND_CHECK_CODE_WRONG =
  'That code did not match. Codes change every 30 seconds, so please wait for a new one and try again.';
export const SECOND_CHECK_CODE_USED =
  'That code has already been used. Please wait for a new one to appear in your app, then enter it.';
export const SECOND_CHECK_CODE_TOO_MANY = 'There have been too many tries for now. Please wait a few minutes, then try again.';
/**
 * The call could not be completed from the browser's side: no answer, or the 15-second wait
 * ran out. It does NOT say nothing was confirmed: a timed-out verify may have reached the
 * server, which is why the line claims only what the browser knows.
 */
export const SECOND_CHECK_NETWORK =
  'We could not reach our server just now. Please check your connection and try again.';
/**
 * The server answered that this account has no passkey this page can use, and there is no
 * authenticator app to fall back on. Not the "browser cannot use a passkey" line, which would
 * be untrue here: the browser can, the account has none.
 */
export const SECOND_CHECK_NO_PASSKEY =
  'We could not find a passkey on your account that works here. Please email Crossmarc support using the address below.';
/** Around the support address, which the prompt draws as a mail link. */
export const SECOND_CHECK_LOST_ACCESS_BEFORE =
  'Lost access to your passkey or your authenticator app? Email Crossmarc support at ';
export const SECOND_CHECK_LOST_ACCESS_AFTER = ' and we will help you back in.';
/** In the form's header, under the receipt and under the second check. */
export const SIGN_OUT_LABEL = 'Sign out';
/**
 * The line above a thank-you screen's primary "Sign out". Defined here, not in the onboarding
 * screens, because the intake form's lazy closure must not import the authenticated client
 * (`intakeBundleImports.test.ts`); onboarding re-exports it.
 */
export const SIGN_OUT_PRIVACY_LINE = 'Signing out helps keep your information private.';
/** On the "Protect your details" screen, so the authenticator app is reachable from the start. */
export const SECOND_CHECK_USE_AUTHENTICATOR = 'Use an authenticator app instead';
export const SECOND_CHECK_USE_PASSKEY = 'Use a passkey instead';

// Tax returns section ----------------------------------------------------------------------

/** The section's title and its rail label. An ordinary numbered section (crossmarc/steward-app#2431). */
export const TAX_RETURNS_TITLE = 'Tax returns';
export const TAX_RETURNS_LIST_LABEL = 'Tax returns added';
export const TAX_RETURNS_UPLOAD_TITLE = 'Add a tax return';
export const TAX_RETURNS_UPLOAD_FORMATS = 'PDF, PNG, JPEG';
export const TAX_RETURNS_REMOVE_QUESTION = 'Remove this tax return?';

// Kept bank connections (crossmarc/steward-app S5, founder decision F2, 2026-10-08) ------------
// Claims here are limited to what the code does. It reads nothing from the bank while a
// connection is kept. It does NOT say the bank is untouched, because Plaid refreshes its own
// copy. The removal questions say what comes off the form and whether the connection ends; they
// do not say who can see what.

/** Over the list of kept banks, where the form shows any. */
export const KEPT_BANKS_HEADING = 'Banks you have connected';
/** Fallback when a kept bank has no name on record. */
export const KEPT_BANK_UNNAMED = 'this bank';
/** The button that ends a connection. */
export const KEPT_BANK_REMOVE_LABEL = 'Remove this bank';
/** Slot U3: the one confirmation before a bank is removed. No undo, and it says so. */
export function keptBankRemoveQuestion(bankName: string, uploads = 0): string {
  return `Remove ${bankName}? This takes its accounts off your form and ends the connection to it.${uploadsGoToo(uploads)} This cannot be undone.`;
}
/**
 * The sentence a removal adds when files the person attached go with it (crossmarc/steward-app#2185):
 * a hand-typed account's, a connected account's, a bank's. One wording for all three, so they cannot
 * drift. Empty for none, and starts with a space otherwise so it can follow a sentence.
 */
export function uploadsGoToo(count: number): string {
  if (count <= 0) return '';
  return count === 1
    ? ' The statement you attached to it will be removed too.'
    : ` The ${count} statements you attached to it will be removed too.`;
}
export const KEPT_BANK_REMOVE_CONFIRM = 'Remove this bank';
export const KEPT_BANK_REMOVE_CANCEL = 'Keep it';
export const KEPT_BANK_REMOVING = 'Removing…';
/** Slot U4: removing one account (not the last) leaves the bank connected. */
export const KEPT_ACCOUNT_REMOVE_QUESTION =
  'Remove this account? Your other accounts from this bank stay.';
/**
 * Slot U4, for the LAST account of a bank: shown before the person confirms, because removing it
 * also ends the connection (`useSignedInDraft.removeItem`, best effort; see its docblock).
 */
export function keptAccountRemoveLastQuestion(uploads = 0): string {
  return `Remove this account? It is the last one from this bank, so the connection to it will end too.${uploadsGoToo(uploads)} This cannot be undone.`;
}
/** Ending the connection did not complete (and a fresh look at the list still shows it). */
export function keptBankRemoveFailed(bankName: string): string {
  return `We could not confirm that ${bankName} was removed, so please treat it as still connected. Please try again in a moment.`;
}
/** The account came off, but the connection to its bank could not be ended. */
export function keptBankAutoEndFailed(bankName: string): string {
  return `The account was removed, but we could not end the connection to ${bankName} just now. You can remove the bank below.`;
}
/** The connection ended and its accounts have not all left the list yet. */
export function keptBankAccountsPending(bankName: string): string {
  return `The connection to ${bankName} has ended. Its accounts may take a moment to leave your list.`;
}
/** The connection ended, but taking some of its accounts off the form could not be started. */
export function keptBankAccountsNotQueued(bankName: string): string {
  return `The connection to ${bankName} has ended, but some of its accounts are still on your form. You can remove each one with its own Remove button.`;
}
/** The connection is gone from the list, but nothing was done for its accounts here, so some may remain. */
export function keptBankEndedAccountsMayRemain(bankName: string): string {
  return `The connection to ${bankName} has ended. Some of its accounts may still be on your form, and you can remove any of them with its own Remove button.`;
}
/** The list of kept banks could not be read, so the block cannot say what is connected. */
export const KEPT_BANKS_UNAVAILABLE =
  'We could not check your connected banks just now. Reloading this page will try again.';


// Host ------------------------------------------------------------------------------------

/**
 * The heading of every "one moment" panel between signing in and the form: the page working
 * out whether the person is signed in or reading an invitation (`IntakeFlow`), and the form
 * waiting on the first read of the saved answers (`IntakeWizard`).
 */
export const FLOW_OPENING_HEADING = 'Opening your form';
/** `IntakeFlow`: nothing is known about the person yet. */
export const FLOW_OPENING_BODY = 'One moment while we get things ready.';
/** `IntakeWizard`: signed in, second check passed, the saved answers are being read. */
export const FORM_OPENING_BODY = 'One moment while we check your saved answers.';
